import { Box } from '@mui/material';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HEADER_DROPDOWN_MENU_ID, HEADER_HEIGHT } from '../../constants/layout';
import { useViewport } from '../../hooks/useViewport';
import { ReactLink } from '../../models/common';
import { StrapiEnTeteEntity } from '../../models/layout';
import theme from '../../config/theme';
import { transformHeaderLinks } from '../../utils/layout';
import { localizeUrl } from '../../utils/tools';
import { IconButton } from '../atoms/IconButton';
import { Image } from '../atoms/Image';
import { Link } from '../atoms/Link';
import { SkipLink } from '../atoms/SkipLink';
import { IconBurgerMenu } from '../icons/IconBurgerMenu';
import { DropdownMenu } from '../molecules/header/DropdownMenu';
import { HeaderLinksDesktop } from '../molecules/header/HeaderLinksDesktop';
import { HeaderActionButtons } from '../molecules/header/HeaderActionButtons';
import { MainBurgerMenu } from '../molecules/header/MainBurgerMenu';
import { Container } from './Container';
import { IconClose } from '../icons/IconClose';

export type HeaderProps = {
  enTete?: StrapiEnTeteEntity;
  locale: string;
};

// Added <Suspense> around components declarations to fix lazy loading on window resize
const Drawer = lazy(() => import('@mui/material/Drawer'));

export const Header = ({ enTete, locale }: HeaderProps) => {
  const { t } = useTranslation();
  const { isSmallViewport } = useViewport();
  const [isMainBurgerMenuOpen, setIsMainBurgerMenuOpen] = useState(false);
  const switchBurgerMenu = useCallback(
    () => setIsMainBurgerMenuOpen(!isMainBurgerMenuOpen),
    [isMainBurgerMenuOpen, setIsMainBurgerMenuOpen]
  );

  const [dropdownLinks, setDropdownLinks] = useState<ReactLink[]>([]);
  const [dropdownIndex, setDropdownIndex] = useState<number | undefined>();

  const handleDropdown = (sublinks: ReactLink[], index?: number) => {
    setDropdownLinks(sublinks);
    setDropdownIndex(index);
  };

  useEffect(() => {
    if (dropdownLinks.length !== 0) {
      //Focus first link of submenu when we open it (for accessibility)
      document.getElementById(HEADER_DROPDOWN_MENU_ID)?.getElementsByTagName('a')[0].focus();
    }
  }, [dropdownLinks]);

  if (!enTete) return null;
  const headerLinks = transformHeaderLinks(enTete.LienMenus ?? []);

  const isAnyDropdownActive = () => {
    return dropdownLinks.length > 0 || dropdownIndex !== undefined;
  };

  const ariaLabelMenuDisplay = t('menu.display');
  return (
    <Box
      component="header"
      sx={{
        zIndex: 1000,
        position: 'sticky',
        top: 0,
        display: 'flex',
        alignItems: 'center',
        height: HEADER_HEIGHT,
        width: '100%',
        backgroundColor: `background.${isMainBurgerMenuOpen ? 'paper' : 'default'}`,
        pt: isSmallViewport ? '2rem' : '1rem',
        pb: isSmallViewport ? '2rem' : 0
      }}
    >
      {/* Desktop Header */}
      {!isSmallViewport && (
        <>
          <Box
            sx={{
              zIndex: 1000,
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              backgroundColor: 'background.default',
              marginTop: '5rem'
            }}
          >
            <Container sx={{ backgroundColor: 'background.default' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Liens d'évitement RGAA */}
                <SkipLink />

                {/* Logo */}
                <Box
                  sx={{
                    flex: '0 0 10rem',
                    width: '10rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Link
                    to={localizeUrl('/')}
                    sx={{
                      // display: 'inline-block',
                      width: '100%',
                      // lineHeight: 0
                    }}
                  >
                    <Image
                      src="/images/logo-melusine-light.png"
                      alt={t('layout.toHomepage') ?? 'logo'}
                      sx={{ height: 'auto', width: '100%' }}
                    />
                  </Link>
                </Box>

                {/* Links */}
                <Box
                  component="nav"
                  role="navigation"
                  sx={{
                    flex: '0 1 65rem',
                    px: '3rem',
                    pr: '3rem',
                    mx: 'auto'
                  }}
                >
                  <HeaderLinksDesktop
                    links={headerLinks}
                    dropdownVisible={isAnyDropdownActive()}
                    setDropdown={(sublinks, index) => {
                      // If there are sublinks, and selected submenu index is not the same
                      // Display sublinks
                      if (sublinks.length !== 0 && index !== dropdownIndex) {
                        handleDropdown(sublinks, index);
                      }
                      // Else, empty dropdown if not already empty
                      else if (dropdownLinks.length !== 0) {
                        handleDropdown([]);
                      }
                    }}
                  />
                </Box>

                {/* Lang, SignUp, SignIn */}
                <Box
                  sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '2rem'
                  }}
                >
                <HeaderActionButtons />
                </Box>
              </Box>
            </Container>
          </Box>
          {isAnyDropdownActive() && <DropdownMenu dropdownLinks={dropdownLinks} onClick={() => handleDropdown([])} />}
        </>
      )}

      {/* Mobile Header */}
      {isSmallViewport && (
        <Container>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItem: 'center'
            }}
          >
            {/* Liens d'évitement RGAA */}
            <SkipLink />

            {/* Logo */}
            <Box
              sx={{
                flex: '0 1 7rem',
                maxWidth: '7rem',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Link to={localizeUrl('/')} sx={{ display: 'inline-block', lineHeight: 0 }}>
                <Image src="/images/logo-melusine-light.png" alt={t('layout.toHomepage') ?? 'logo'} sx={{ height: '7rem' }} />
              </Link>
            </Box>

            {/* Main burger menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                aria-label={ariaLabelMenuDisplay}
                disableRipple
                size="medium"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: '-0.75rem',
                  svg: {
                    color: 'primary.main'
                  },
                  '&:focus, &:focus-visible': {
                    backgroundColor: theme.palette.secondary.main,
                    boxShadow: 'none',
                    svg: {
                      line: {
                        stroke: 'white'
                      }
                    }
                  }
                }}
                onClick={() => {
                  switchBurgerMenu();
                }}
              >
                { isMainBurgerMenuOpen ? <IconClose/> : <IconBurgerMenu/>}
              </IconButton>
            </Box>
          </Box>
          <Suspense>
            <Drawer
              slotProps={{
                paper: {
                  sx: {
                    width: '100%',
                    mt: '7rem',
                    pb: '10rem',
                    boxShadow: 'none'
                  }
                },
                backdrop:{
                  sx: {
                    mt: '7rem'
                  }
                },
                root: {
                    sx: {
                    mt: '7rem'
                  }
                }
              }}
              anchor="left"
              open={isMainBurgerMenuOpen}
            >
              <MainBurgerMenu
                headerLinks={headerLinks}
                onMenuClose={() => setIsMainBurgerMenuOpen(false)}
              />
            </Drawer>
          </Suspense>
        </Container>
      )}
    </Box>
  );
};
